import { useEffect, useState } from "react";
import { Plus, Search } from "lucide-react";
import { Button } from "../../components/ui";

export type StatusFilter = "all" | "active" | "inactive";

export interface ToolbarFilters {
  search: string;
  type: string;
  state: string;
  status: StatusFilter;
}

interface TypeOption {
  value: string;
  label: string;
}

interface AgreementsToolbarProps {
  filters: ToolbarFilters;
  onFiltersChange: (next: ToolbarFilters) => void;
  onNewAgreement: () => void;
  typeOptions: TypeOption[];
  stateOptions: string[];
}

export function AgreementsToolbar({
  filters,
  onFiltersChange,
  onNewAgreement,
  typeOptions,
  stateOptions,
}: AgreementsToolbarProps) {
  // Debounce the search input so the parent doesn't re-render on every keystroke
  const [searchDraft, setSearchDraft] = useState(filters.search);

  useEffect(() => {
    if (searchDraft === filters.search) return;
    const id = window.setTimeout(() => {
      onFiltersChange({ ...filters, search: searchDraft });
    }, 200);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft]);

  // If filters reset externally (e.g. clear), sync back into the draft.
  useEffect(() => {
    if (filters.search !== searchDraft) setSearchDraft(filters.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  return (
    <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search
            size={13}
            strokeWidth={2}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-5 pointer-events-none"
          />
          <input
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            placeholder="Search agreements..."
            aria-label="Search agreements"
            className="pl-8 pr-3 py-2 rounded-md hair border bg-paper-hi text-[12.5px] w-72 placeholder:text-ink-5 focus:outline-none focus:ring-2 focus:ring-tide/40"
          />
        </div>

        <select
          aria-label="Filter by agreement type"
          value={filters.type}
          onChange={(e) => onFiltersChange({ ...filters, type: e.target.value })}
          className="px-2.5 py-2 rounded-md hair border bg-paper-hi text-[12px] text-ink-3"
        >
          <option value="">All types</option>
          {typeOptions.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>

        <select
          aria-label="Filter by state"
          value={filters.state}
          onChange={(e) =>
            onFiltersChange({ ...filters, state: e.target.value })
          }
          className="px-2.5 py-2 rounded-md hair border bg-paper-hi text-[12px] text-ink-3"
        >
          <option value="">All states</option>
          {stateOptions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <select
          aria-label="Filter by status"
          value={filters.status}
          onChange={(e) =>
            onFiltersChange({
              ...filters,
              status: e.target.value as StatusFilter,
            })
          }
          className="px-2.5 py-2 rounded-md hair border bg-paper-hi text-[12px] text-ink-3"
        >
          <option value="all">All statuses</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>
      </div>

      <Button
        variant="primary"
        icon={<Plus size={13} strokeWidth={2.2} />}
        onClick={onNewAgreement}
      >
        New agreement
      </Button>
    </div>
  );
}
